import "../styles/tailwind.css";
import "./styles.css";
// Adds a polyfill if needed
import "core-js/stable/global-this";
import { install } from "resize-observer";
if (typeof window !== "undefined") {
  if (typeof BigInt === "undefined") {
    global.BigInt = require("bignumber.js");
  }
  if (typeof ResizeObserver === "undefined") {
    install();
  }
}

// @ts-ignore
import "cropperjs/dist/cropper.css";
import "react-loading-skeleton/dist/skeleton.css";
import "react-phone-input-2/lib/high-res.css";
import "../../public/Calendar.css";
import "../styles/editor.css";
import "../styles/player-temp.css";
import "../styles/scrollbar.css";
// import "../styles/player-temp.css";

import { usePageViewTracking } from "@hooks/posthog/usePageViewTracking";
import Providers from "@providers/Providers";
import { ErrorBoundary } from "@sentry/nextjs";
import { queryClient } from "@services/queryClient";
import { globalStyles } from "@styles/global";
import { Hydrate, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { isProduction } from "@utils/utils";
import { Analytics } from "@vercel/analytics/react";
import "@vidstack/react/player/styles/base.css";
import "core-js/stable/global-this";
import { NextPage } from "next";
import { AppProps } from "next/app";
import Router from "next/router";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import { setConfiguration } from "react-grid-system";
import TagManager from "react-gtm-module";
import Custom505 from "./500";

globalStyles();

setConfiguration({
  gutterWidth: 16,
  gridColumns: 12,
  breakpoints: [576, 768, 992, 1200, 1400],
  containerWidths: [540, 720, 960, 1140, 1320],
});

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
  provider?: any;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

interface DefaultProviderProps {
  children: ReactNode;
}

const DefaultProvider = ({ children }: DefaultProviderProps) => <>{children}</>;

const AUTO_CAPTURE_ENABLED = true;
const isProd = process.env.NODE_ENV === "production";
const hasKey = !!process.env.NEXT_PUBLIC_POSTHOG_KEY;

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const [queryClientState] = useState(queryClient);
  const getLayout = Component.getLayout ?? (page => page);
  const ContextProvider = Component.provider || DefaultProvider;

  useEffect(() => {
    isProduction() && TagManager.initialize({ gtmId: "GTM-WCB4XXPW" });
  }, []);

  useEffect(() => {
    if (!isProd || !hasKey) return;

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: "/ingest",
      ui_host: "https://us.posthog.com",
      capture_pageview: AUTO_CAPTURE_ENABLED,
      debug: false,
      loaded: ph => {
        if (!isProd) ph.debug();
      },
    });

    if (!AUTO_CAPTURE_ENABLED) {
      const handleRouteChange = () => posthog.capture("$pageview");
      Router.events.on("routeChangeComplete", handleRouteChange);
      return () => Router.events.off("routeChangeComplete", handleRouteChange);
    }
  }, []);

  usePageViewTracking();

  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClientState}>
        <Hydrate state={pageProps.dehydratedState}>
          <ErrorBoundary fallback={<Custom505 />}>
            <Providers pageProps={pageProps}>
              <ContextProvider>
                <div id="modal-app" />
                {getLayout(
                  <>
                    <Component {...pageProps} />
                    <Analytics />
                  </>
                )}
                <div id="menu-bottom" />
              </ContextProvider>
              <ReactQueryDevtools initialIsOpen={false} />
            </Providers>
          </ErrorBoundary>
        </Hydrate>
      </QueryClientProvider>
    </PostHogProvider>
  );
}
